import DiscoverIconSM from "@/components/DiscoverIconSm";
import { apiUrl } from "@/constants";
import { BlogPost } from "@/types";
import {
  Center,
  Container,
  Divider,
  Heading,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { GetStaticProps } from "next";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";

interface BlogIndexProps {
  blogs: BlogPost[];
}

const getPosts = async () => {
  const res = await fetch(`${apiUrl}/ninja/blogs`);
  const posts: BlogPost[] = await res.json();
  return posts;
};

export default function BlogIndex({ blogs }: BlogIndexProps) {
  const { data: posts } = useQuery({
    queryKey: ["posts"],
    queryFn: getPosts,
    initialData: blogs,
  });

  return (
    <>
      <Head>
        <title>Songcards Spotlight</title>
        <meta
          name="description"
          content="Stories from the humans that make it all possible."
        />
        <meta itemProp="image" content="https://songcards.io/sc-pink.png" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta property="og:title" content="Songcards Spotlight" />
        <meta
          property="og:description"
          content="Stories from the humans that make it all possible."
        />
        <meta property="og:url" content="https://songcards.io/blog" />
        <meta property="og:image" content="https://songcards.io/sc-pink.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Songcards Spotlight" />
        <meta
          name="twitter:description"
          content="Stories from the humans that make it all possible."
        />
        <meta name="twitter:image" content="https://songcards.io/sc-pink.png" />
        <link rel="icon" href="/songcards.png" />
        <link rel="apple-touch-icon" href="/icon.png" />
      </Head>
      <Container maxW="3xl" p={4} mb={100}>
        <Heading textAlign="center" mt={0} mb={10}>
          Songcards Spotlight
        </Heading>
        <Text textAlign="center" mt={2} mb={10}>
          Stories from the humans that make it all possible
        </Text>
        <br />
        <Center>
          <VStack spacing={10} align="stretch" maxW={"md"}>
            {posts.map((post: BlogPost) => (
              <Center key={post.slug}>
                <Link href={`/blog/${post.slug}`} passHref>
                  <VStack
                    spacing={4}
                    align="start"
                    _hover={{ textDecoration: "none", opacity: 0.9 }}
                  >
                    {post.image && (
                      <Image
                        src={post.image}
                        alt={post.title}
                        width={500}
                        height={500}
                        style={{
                          width: 500,
                          height: 500,
                          borderRadius: "2%",
                          objectFit: "cover",
                          borderColor: "white",
                          borderStyle: "solid",
                        }}
                      />
                    )}
                    <Heading size="md">{post.title}</Heading>
                    <Divider mt={5} mb={0} />
                  </VStack>
                </Link>
              </Center>
            ))}
            <DiscoverIconSM />
          </VStack>
        </Center>
      </Container>
    </>
  );
}

export const getStaticProps: GetStaticProps = async () => {
  const blogs: BlogPost[] = await getPosts();

  return {
    props: {
      blogs,
    },
    revalidate: 86400,
  };
};
